import { useCallback, useEffect, useState } from "react";
import { api, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { DataStateRow } from "@/components/ui/data-state";
import { toast } from "sonner";
import { Plus, RefreshCcw, Upload, FileText, ChevronRight, Pencil } from "lucide-react";
import EditDocumentDialog from "@/components/legal-vault/EditDocumentDialog";

// ---------------------------------------------------------------------------
// Document type form
// ---------------------------------------------------------------------------
const emptyDocTypeForm = { code: "", name: "", description: "", is_requestable: true };

function DocTypeDialog({ open, onClose, onSaved, entityId }) {
  const [form, setForm] = useState(emptyDocTypeForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(emptyDocTypeForm);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/legal/document-types", { ...form, entity_id: entityId });
      toast.success("Đã tạo loại giấy tờ");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(parseApiError(err, "Không tạo được loại giấy tờ").message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Thêm loại giấy tờ</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label>Mã loại giấy tờ</Label>
            <Input required value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="VD: DANG_KY_KD" />
          </div>
          <div>
            <Label>Tên loại giấy tờ (tiếng Việt)</Label>
            <Input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="VD: Giấy đăng ký kinh doanh" />
          </div>
          <div>
            <Label>Mô tả</Label>
            <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="is_requestable" checked={form.is_requestable}
              onChange={(e) => setForm((f) => ({ ...f, is_requestable: e.target.checked }))} />
            <Label htmlFor="is_requestable">Cho phép nhân sự yêu cầu</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Huỷ</Button>
            <Button type="submit" disabled={saving}>{saving ? "Đang lưu…" : "Tạo loại giấy tờ"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Upload document dialog
// ---------------------------------------------------------------------------
function UploadDialog({ open, onClose, onSaved, docTypeId, entityId }) {
  const [file, setFile] = useState(null);
  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setFile(null); setIssueDate(""); setExpiryDate(""); setNotes(""); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Chọn file tài liệu"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_id", entityId);
      fd.append("document_type_id", docTypeId);
      if (issueDate) fd.append("issue_date", issueDate);
      if (expiryDate) fd.append("expiry_date", expiryDate);
      if (notes) fd.append("notes", notes);
      await api.post("/legal/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Đã upload tài liệu");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(parseApiError(err, "Không upload được").message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload tài liệu</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label>File tài liệu</Label>
            <Input type="file" required onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Ngày hiệu lực</Label>
              <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
            </div>
            <div>
              <Label>Ngày hết hạn</Label>
              <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Ghi chú</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Huỷ</Button>
            <Button type="submit" disabled={saving}>{saving ? "Đang upload…" : "Upload"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function LegalVault() {
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [docTypeDialog, setDocTypeDialog] = useState(false);
  const [uploadDialog, setUploadDialog] = useState({ open: false, docTypeId: null });
  const [editDocDialog, setEditDocDialog] = useState({ open: false, document: null });

  const loadEntities = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/legal/entities");
      setEntities(r.data || []);
      if (r.data?.length && !selectedEntity) setSelectedEntity(r.data[0]);
    } catch (e) {
      setError(parseApiError(e, "Không tải được danh sách pháp nhân").message);
    } finally {
      setLoading(false);
    }
  }, [selectedEntity]);

  const loadDocTypes = useCallback(async () => {
    if (!selectedEntity) return;
    try {
      const r = await api.get(`/legal/document-types?entity_id=${selectedEntity.id}`);
      setDocTypes(r.data || []);
    } catch (e) {
      toast.error(parseApiError(e, "Không tải được loại giấy tờ").message);
    }
  }, [selectedEntity]);

  const loadDocuments = useCallback(async () => {
    if (!selectedEntity) return;
    try {
      const r = await api.get(`/legal/documents?entity_id=${selectedEntity.id}`);
      setDocuments(r.data || []);
    } catch (e) {
      // non-fatal
    }
  }, [selectedEntity]);

  useEffect(() => { loadEntities(); }, []);
  useEffect(() => { if (selectedEntity) { loadDocTypes(); loadDocuments(); } }, [selectedEntity]);

  return (
    <div data-testid="page-legal-vault">
      <PageHeader
        kicker="Admin • Kho hồ sơ pháp nhân"
        title="Kho tài liệu pháp lý"
        breadcrumb={["Pháp lý", "Kho tài liệu"]}
        actions={
          <Button type="button" variant="outline" onClick={() => { loadDocTypes(); loadDocuments(); }} className="rounded-sm">
            <RefreshCcw className="w-4 h-4 mr-1.5" />Tải lại
          </Button>
        }
      />

      <div className="p-6 lg:p-8 flex gap-6">
        {/* Entity sidebar */}
        <div className="w-56 flex-shrink-0 space-y-1">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Pháp nhân</p>
          {loading && <DataStateRow loading />}
          {error && <p className="text-red-600 text-sm">{error}</p>}
          {entities.map((e) => (
            <button
              key={e.id}
              onClick={() => setSelectedEntity(e)}
              className={`w-full text-left px-3 py-2 rounded-sm text-sm flex items-center gap-1.5 transition-colors ${selectedEntity?.id === e.id ? "bg-stone-900 text-white" : "hover:bg-stone-100 text-stone-700"}`}
            >
              <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{e.name}</span>
            </button>
          ))}
        </div>

        {/* Right panel */}
        <div className="flex-1 min-w-0 space-y-6">
          {selectedEntity && (
            <>
              {/* Document types */}
              <div className="bg-white border border-stone-200 rounded-sm">
                <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
                  <h3 className="font-medium text-stone-800">Danh mục loại giấy tờ — {selectedEntity.name}</h3>
                  <Button size="sm" onClick={() => setDocTypeDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" />Thêm loại
                  </Button>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-100 text-stone-500 text-xs">
                      <th className="px-4 py-2 text-left">Mã</th>
                      <th className="px-4 py-2 text-left">Tên</th>
                      <th className="px-4 py-2 text-left">Có thể yêu cầu</th>
                      <th className="px-4 py-2 text-left">Trạng thái</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {docTypes.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-stone-400">Chưa có loại giấy tờ</td></tr>
                    )}
                    {docTypes.map((dt) => (
                      <tr key={dt.id} className="border-b border-stone-50 hover:bg-stone-50">
                        <td className="px-4 py-2 font-mono text-xs">{dt.code}</td>
                        <td className="px-4 py-2">{dt.name}</td>
                        <td className="px-4 py-2">{dt.is_requestable ? "✓" : "—"}</td>
                        <td className="px-4 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${dt.is_active ? "bg-green-50 text-green-700" : "bg-stone-100 text-stone-500"}`}>
                            {dt.is_active ? "Hoạt động" : "Tắt"}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button size="sm" variant="outline"
                            onClick={() => setUploadDialog({ open: true, docTypeId: dt.id })}>
                            <Upload className="w-3.5 h-3.5 mr-1" />Upload
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Documents */}
              <div className="bg-white border border-stone-200 rounded-sm">
                <div className="px-4 py-3 border-b border-stone-100">
                  <h3 className="font-medium text-stone-800">Tài liệu đã lưu — {selectedEntity.name}</h3>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-100 text-stone-500 text-xs">
                      <th className="px-4 py-2 text-left">Tên file</th>
                      <th className="px-4 py-2 text-left">Phiên bản</th>
                      <th className="px-4 py-2 text-left">Ngày HH</th>
                      <th className="px-4 py-2 text-left">Ngày hết hạn</th>
                      <th className="px-4 py-2 text-left">Trạng thái</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {documents.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-6 text-center text-stone-400">Chưa có tài liệu nào</td></tr>
                    )}
                    {documents.map((doc) => (
                      <tr key={doc.id} className="border-b border-stone-50 hover:bg-stone-50"
                        data-testid={`legal-doc-row-${doc.id}`}>
                        <td className="px-4 py-2 flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5 text-stone-400" />
                          <span className="truncate max-w-[200px]">{doc.file_name}</span>
                        </td>
                        <td className="px-4 py-2">v{doc.version_no}</td>
                        <td className="px-4 py-2">{doc.issue_date || "—"}</td>
                        <td className="px-4 py-2">{doc.expiry_date || "—"}</td>
                        <td className="px-4 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${doc.status === "active" ? "bg-green-50 text-green-700" : "bg-stone-100 text-stone-500"}`}>
                            {doc.status === "active" ? "Hiệu lực" : doc.status === "superseded" ? "Thay thế" : "Hết hạn"}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button size="sm" variant="outline"
                            onClick={() => setEditDocDialog({ open: true, document: doc })}
                            data-testid={`legal-doc-edit-${doc.id}`}>
                            <Pencil className="w-3.5 h-3.5 mr-1" />Sửa
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <DocTypeDialog
        open={docTypeDialog}
        onClose={() => setDocTypeDialog(false)}
        onSaved={loadDocTypes}
        entityId={selectedEntity?.id}
      />

      <UploadDialog
        open={uploadDialog.open}
        onClose={() => setUploadDialog({ open: false, docTypeId: null })}
        onSaved={loadDocuments}
        docTypeId={uploadDialog.docTypeId}
        entityId={selectedEntity?.id}
      />

      <EditDocumentDialog
        open={editDocDialog.open}
        onClose={() => setEditDocDialog({ open: false, document: null })}
        onSaved={loadDocuments}
        document={editDocDialog.document}
      />
    </div>
  );
}
